import { useContext } from 'react'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { ListPoliciesRequest } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useVendors } from 'src/pages/Vendors/vendors.hooks'
import { useRisks } from 'src/pages/Risks/risks.hooks'
import { useControlStatuses } from 'src/pages/AccountSettings/AccountSettings.hooks'
import { useDepartments } from '../../Department/useDepartments'
import { useSwrImmutableGrpc } from '../../useSwrImmutableGrpc'
import { DownloadControlsReport } from '../../../../Utils/reports/ControlsReport'
import { AuditContext } from '../../../../context/AuditContext'
import { useAudit } from '../audit'
import { boolValue, stringValue } from '../../../../Utils/primitives'
import { useComplianceFrameworks } from '../../complianceframework'
import { useComplianceFrameworkObjectives } from '../../Framework/useComplianceFrameworkObjectives'

export const useDownloadControlsReport = (
  auditId?: string,
): (() => Promise<void>) => {
  const departments = useDepartments()
  const { auditId: currentAuditId } = useContext(AuditContext)
  const { data: audit } = useAudit(auditId ?? '', !!auditId)
  const { data: complianceFrameworks } = useComplianceFrameworks(true)
  const { data: cfObjectives } = useComplianceFrameworkObjectives(complianceFrameworks?.getItemsList()?.map((cf) => cf.getId()) ?? [])
  const { data: vendors } = useVendors()
  const { data: risksData } = useRisks()
  const { data: controlStatuses } = useControlStatuses()


  const invalidAuditId = auditId && !audit

  const {
    response: { data: controls },
  } = useSwrImmutableGrpc(
    ModelPromiseClient.prototype.listControls,
    new ListControlsRequest()
      .setIsDismissed(boolValue(false))
      .setAuditId(stringValue(auditId ?? currentAuditId))
      .setUseNewModel(true),
    !invalidAuditId,
  )

  const request = new ListPoliciesRequest().setUseNewModel(true)

  const {
    response: { data: policies },
  } = useSwrImmutableGrpc(
    PolicyPromiseClient.prototype.list,
    request,
    !invalidAuditId,
  )
  if (invalidAuditId) {
    return () => Promise.resolve()
  }

  const controlPolicyNamesByControlId = (controls?.getItemsList() || []).reduce(
    (acc: Record<string, string[]>, control) => {
      acc[control.getId()] = [
        ...new Set(
          control
            .getLinkedPoliciesList()
            .map((policy) => policy.getPolicyName())
            .filter(Boolean),
        ),
      ]
      return acc
    },
    {},
  )
  // One department entry per linked policy means a control with several policies
  // in the same department would repeat it; dedupe so the export lists it once.
  const controlDepartmentNamesByControlId = (
    controls?.getItemsList() || []
  ).reduce((acc: Record<string, string[]>, control) => {
    acc[control.getId()] = [
      ...new Set(
        control
          .getLinkedPoliciesList()
          .map((policy) => policy.getDepartment())
          .filter(Boolean),
      ),
    ]
    return acc
  }, {})

  const risks = risksData?.getRisksList()

  return () =>
    DownloadControlsReport(
      controls,
      policies,
      controlPolicyNamesByControlId,
      controlDepartmentNamesByControlId,
      departments,
      vendors?.getVendorsList(),
      complianceFrameworks,
      cfObjectives,
      risks,
      controlStatuses?.getItemsList(),
    )
}
